/** Thin fetch wrapper. All API routes are under /api; cookies sent with every call. */
export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    let message = `Request failed (${res.status})`;
    try {
      const body = (await res.json()) as { message?: string };
      if (body?.message) message = body.message;
    } catch {
      /* non-JSON error body */
    }
    throw new ApiError(res.status, message);
  }
  return res.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

export interface CurrentUser {
  id: string;
  email: string;
  name: string;
  title: string | null;
  role: "admin" | "member";
  avatarUrl: string | null;
}

export const getMe = () => api<CurrentUser>("/auth/me");

export const login = (email: string, password: string) =>
  api<CurrentUser>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });

export const logout = () => api<{ ok: boolean }>("/auth/logout", { method: "POST" });

/** Update your own display name. */
export const updateMe = (name: string) =>
  api<CurrentUser>("/auth/me", { method: "PATCH", body: JSON.stringify({ name }) });

export const changePassword = (currentPassword: string, newPassword: string) =>
  api<{ ok: boolean }>("/auth/change-password", {
    method: "POST",
    body: JSON.stringify({ currentPassword, newPassword }),
  });

export async function uploadAvatar(file: File): Promise<CurrentUser> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch("/api/users/me/avatar", {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const b = (await res.json()) as { message?: string };
      if (b?.message) message = b.message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json() as Promise<CurrentUser>;
}

export const deleteAvatar = () =>
  api<CurrentUser>("/users/me/avatar", { method: "DELETE" });

// ── Admin: team management ─────────────────────────────────────────────
export interface TeamUser {
  id: string;
  email: string;
  name: string;
  title: string | null;
  description: string | null;
  role: "admin" | "member";
  active: boolean;
  avatarUrl: string | null;
  createdAt: string;
}

export const listUsers = () => api<TeamUser[]>("/users");

export const createUser = (input: {
  name: string;
  email: string;
  password: string;
  title?: string;
  role: "admin" | "member";
}) => api<TeamUser>("/users", { method: "POST", body: JSON.stringify(input) });

export const updateUser = (
  id: string,
  fields: {
    name?: string;
    title?: string | null;
    description?: string | null;
    role?: "admin" | "member";
    active?: boolean;
  },
) => api<TeamUser>(`/users/${id}`, { method: "PATCH", body: JSON.stringify(fields) });

// ── Team targets (view: everyone · edit: admin) ────────────────────────
export interface UserTarget {
  id: string;
  userId: string;
  description: string;
  /** Equity granted on achieving the target, in percent (0.5 = 0.5%). */
  equityPct: number;
  createdAt: string;
}

export const listUserTargets = (userId: string) =>
  api<UserTarget[]>(`/user-targets/${userId}`);

export const createUserTarget = (
  userId: string,
  input: { description: string; equityPct: number },
) =>
  api<UserTarget>(`/user-targets/${userId}`, {
    method: "POST",
    body: JSON.stringify(input),
  });

export const updateUserTarget = (
  id: string,
  fields: { description?: string; equityPct?: number },
) =>
  api<UserTarget>(`/user-targets/target/${id}`, {
    method: "PATCH",
    body: JSON.stringify(fields),
  });

export const deleteUserTarget = (id: string) =>
  api<{ ok: boolean }>(`/user-targets/target/${id}`, { method: "DELETE" });

// ── Audit log (admin) ──────────────────────────────────────────────────
export interface AuditEntry {
  id: string;
  actorId: string | null;
  action: string;
  entityType: string;
  entityId: string | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export const listAuditLogs = (limit = 100) =>
  api<AuditEntry[]>(`/audit?limit=${limit}`);

// ── Board ──────────────────────────────────────────────────────────────
export type TicketStatus = "backlog" | "todo" | "in_progress" | "review" | "done";
export type TicketPriority = "low" | "medium" | "high" | "urgent";

export interface Project {
  id: string;
  key: string;
  name: string;
  description: string | null;
  ticketCount: number;
  doneCount: number;
  createdAt: string;
}

export interface Member {
  id: string;
  name: string;
  title: string | null;
  description: string | null;
  avatarUrl: string | null;
}

export interface Attachment {
  id: string;
  filename: string;
  mime: string;
  size: number;
  createdAt: string;
}

export interface Ticket {
  id: string;
  projectId: string;
  number: number;
  title: string;
  description: string | null;
  solution: string | null;
  status: TicketStatus;
  priority: TicketPriority;
  assigneeId: string | null;
  assignee: { id: string; name: string; avatarUrl: string | null } | null;
  assignedBy: { id: string; name: string; avatarUrl: string | null } | null;
  attachments?: Attachment[];
  createdAt: string;
  updatedAt: string;
}

export const listProjects = () => api<Project[]>("/board/projects");
export const createProject = (input: { key: string; name: string; description?: string }) =>
  api<Project>("/board/projects", { method: "POST", body: JSON.stringify(input) });
export const deleteProject = (id: string) =>
  api<{ ok: boolean }>(`/board/projects/${id}`, { method: "DELETE" });

export const listMembers = () => api<Member[]>("/board/members");

export const listTickets = (projectId: string, opts?: { mine?: boolean }) =>
  api<Ticket[]>(`/board/projects/${projectId}/tickets${opts?.mine ? "?mine=true" : ""}`);
export const getTicket = (id: string) => api<Ticket>(`/board/tickets/${id}`);
export const createTicket = (
  projectId: string,
  input: {
    title: string;
    description?: string;
    assigneeId?: string | null;
    status?: TicketStatus;
    priority?: TicketPriority;
  },
) =>
  api<Ticket>(`/board/projects/${projectId}/tickets`, {
    method: "POST",
    body: JSON.stringify(input),
  });
export const updateTicket = (
  id: string,
  fields: Partial<
    Pick<Ticket, "title" | "description" | "solution" | "status" | "priority" | "assigneeId">
  >,
) => api<Ticket>(`/board/tickets/${id}`, { method: "PATCH", body: JSON.stringify(fields) });
export const deleteTicket = (id: string) =>
  api<{ ok: boolean }>(`/board/tickets/${id}`, { method: "DELETE" });

export const attachmentUrl = (id: string) => `/api/board/attachments/${id}`;
export const deleteAttachment = (id: string) =>
  api<{ ok: boolean }>(`/board/attachments/${id}`, { method: "DELETE" });

// ── CRM ────────────────────────────────────────────────────────────────
export type CustomerStatus = "lead" | "active" | "inactive";
export type CustomerKind = "company" | "individual";
export type DealStage = "lead" | "contacted" | "proposal" | "negotiation" | "won" | "lost";

export interface CustomerOwner {
  id: string;
  name: string;
  avatarUrl: string | null;
}

export interface CustomerRow {
  id: string;
  name: string;
  status: CustomerStatus;
  kind: CustomerKind;
  lastSessionAt: string | null;
  website: string | null;
  industry: string | null;
  logoUrl: string | null;
  owner: CustomerOwner | null;
  contactCount: number;
  openDeals: number;
  openValue: number;
  wonValue: number;
  createdAt: string;
}

export interface CrmContact {
  id: string;
  customerId: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string | null;
  createdAt: string;
}

export interface CustomerDeal {
  id: string;
  customerId: string;
  title: string;
  value: number | null;
  stage: DealStage;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CrmSessionEntry {
  id: string;
  customerId: string;
  date: string;
  description: string;
  createdById: string | null;
  createdAt: string;
}

export interface CustomerDetail {
  id: string;
  name: string;
  status: CustomerStatus;
  kind: CustomerKind;
  sessions: CrmSessionEntry[];
  website: string | null;
  industry: string | null;
  notes: string | null;
  ownerId: string | null;
  owner: CustomerOwner | null;
  logoUrl: string | null;
  contacts: CrmContact[];
  deals: CustomerDeal[];
  createdAt: string;
  updatedAt: string;
}

export interface PipelineDeal extends CustomerDeal {
  customerName: string;
}

export const listCustomers = () => api<CustomerRow[]>("/crm/customers");
export const getCustomer = (id: string) => api<CustomerDetail>(`/crm/customers/${id}`);
export const createCustomer = (input: {
  name: string;
  status?: CustomerStatus;
  kind?: CustomerKind;
  website?: string;
  industry?: string;
  notes?: string;
  ownerId?: string;
}) => api<CustomerDetail>("/crm/customers", { method: "POST", body: JSON.stringify(input) });
export const updateCustomer = (
  id: string,
  fields: Partial<
    Pick<
      CustomerDetail,
      "name" | "status" | "kind" | "website" | "industry" | "notes" | "ownerId"
    >
  >,
) => api<CustomerDetail>(`/crm/customers/${id}`, { method: "PATCH", body: JSON.stringify(fields) });

export async function uploadCustomerLogo(
  customerId: string,
  file: File,
): Promise<{ id: string; logoUrl: string }> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/crm/customers/${customerId}/logo`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const b = (await res.json()) as { message?: string };
      if (b?.message) message = b.message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json() as Promise<{ id: string; logoUrl: string }>;
}

export const deleteCustomerLogo = (customerId: string) =>
  api<{ id: string; logoUrl: null }>(`/crm/customers/${customerId}/logo`, {
    method: "DELETE",
  });
export const deleteCustomer = (id: string) =>
  api<{ ok: boolean }>(`/crm/customers/${id}`, { method: "DELETE" });

export const addContact = (
  customerId: string,
  input: { name: string; email?: string; phone?: string; role?: string },
) =>
  api<CrmContact>(`/crm/customers/${customerId}/contacts`, {
    method: "POST",
    body: JSON.stringify(input),
  });
export const deleteContact = (id: string) =>
  api<{ ok: boolean }>(`/crm/contacts/${id}`, { method: "DELETE" });

export const addCrmSession = (
  customerId: string,
  input: { date: string; description: string },
) =>
  api<CrmSessionEntry>(`/crm/customers/${customerId}/sessions`, {
    method: "POST",
    body: JSON.stringify(input),
  });
export const deleteCrmSession = (id: string) =>
  api<{ ok: boolean }>(`/crm/sessions/${id}`, { method: "DELETE" });

export const listDeals = () => api<PipelineDeal[]>("/crm/deals");
export const createDeal = (input: {
  customerId: string;
  title: string;
  value?: number;
  stage?: DealStage;
  notes?: string;
}) => api<CustomerDeal>("/crm/deals", { method: "POST", body: JSON.stringify(input) });
export const updateDeal = (
  id: string,
  fields: Partial<Pick<CustomerDeal, "title" | "value" | "stage" | "notes" | "customerId">>,
) => api<CustomerDeal>(`/crm/deals/${id}`, { method: "PATCH", body: JSON.stringify(fields) });
export const deleteDeal = (id: string) =>
  api<{ ok: boolean }>(`/crm/deals/${id}`, { method: "DELETE" });

// ── Investors ──────────────────────────────────────────────────────────
// Our own fundraising, deliberately separate from the sales pipeline: the
// money flows the other way, so these must never land in deal rollups.
export type InvestorStage =
  | "lead"
  | "conversation"
  | "diligence"
  | "term_sheet"
  | "committed"
  | "passed";
export type InvestorType =
  | "angel"
  | "vc"
  | "grant"
  | "accelerator"
  | "friends_family"
  | "debt"
  | "revenue_share";
export type InvestorInstrument =
  | "equity"
  | "safe"
  | "convertible"
  | "grant"
  | "debt"
  | "revenue_share";
export type ValuationBasis = "pre" | "post";
export type Confidence = "low" | "medium" | "high";

export interface InvestorNote {
  id: string;
  investorId: string;
  date: string;
  description: string;
  createdById: string | null;
  createdAt: string;
}

export interface Investor {
  id: string;
  name: string;
  type: InvestorType;
  website: string | null;
  logoUrl: string | null;
  contactName: string | null;
  contactRole: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  source: string | null;
  ownerId: string | null;
  owner: CustomerOwner | null;
  amount: number | null;
  equityPct: number | null;
  instrument: InvestorInstrument;
  valuationBasis: ValuationBasis;
  supportMonths: number | null;
  nonFinancialTerms: string | null;
  conditions: string | null;
  costs: number | null;
  round: string | null;
  stage: InvestorStage;
  confidence: Confidence;
  nextStep: string | null;
  nextStepOn: string | null;
  lastContactedOn: string | null;
  decisionBy: string | null;
  revisitOn: string | null;
  passReason: string | null;
  notes: string | null;
  lastActivityOn: string | null;
  noteCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface InvestorDetail extends Investor {
  activity: InvestorNote[];
}

/** Every field the drawer can edit; the backend treats null as "clear it". */
export type InvestorFields = Partial<
  Omit<
    Investor,
    "id" | "owner" | "logoUrl" | "lastActivityOn" | "noteCount" | "createdAt" | "updatedAt"
  >
>;

export const listInvestors = () => api<Investor[]>("/investors");
export const getInvestor = (id: string) => api<InvestorDetail>(`/investors/${id}`);
export const createInvestor = (input: InvestorFields & { name: string }) =>
  api<InvestorDetail>("/investors", { method: "POST", body: JSON.stringify(input) });
export const updateInvestor = (id: string, fields: InvestorFields) =>
  api<InvestorDetail>(`/investors/${id}`, { method: "PATCH", body: JSON.stringify(fields) });
export const deleteInvestor = (id: string) =>
  api<{ ok: boolean }>(`/investors/${id}`, { method: "DELETE" });

export const addInvestorNote = (
  investorId: string,
  input: { date: string; description: string },
) =>
  api<InvestorNote>(`/investors/${investorId}/notes`, {
    method: "POST",
    body: JSON.stringify(input),
  });
export const deleteInvestorNote = (noteId: string) =>
  api<{ ok: boolean }>(`/investors/notes/${noteId}`, { method: "DELETE" });

export async function uploadInvestorLogo(
  investorId: string,
  file: File,
): Promise<{ id: string; logoUrl: string }> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/investors/${investorId}/logo`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const b = (await res.json()) as { message?: string };
      if (b?.message) message = b.message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json() as Promise<{ id: string; logoUrl: string }>;
}

export const deleteInvestorLogo = (investorId: string) =>
  api<{ id: string; logoUrl: null }>(`/investors/${investorId}/logo`, { method: "DELETE" });

// ── Competition ────────────────────────────────────────────────────────
export interface CompetitorFile {
  id: string;
  competitorId: string;
  folderId: string | null;
  filename: string;
  mime: string;
  size: number;
  uploadedById: string | null;
  createdAt: string;
}

export interface CompetitorFolder {
  id: string;
  competitorId: string;
  parentId: string | null;
  name: string;
  createdAt: string;
}

export interface CompetitorRow {
  id: string;
  name: string;
  description: string | null;
  website: string | null;
  logoUrl: string | null;
  fileCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface CompetitorDetail {
  id: string;
  name: string;
  description: string | null;
  website: string | null;
  logoUrl: string | null;
  folders: CompetitorFolder[];
  files: CompetitorFile[];
  createdAt: string;
  updatedAt: string;
}

export const listCompetitors = () => api<CompetitorRow[]>("/competition/competitors");
export const getCompetitor = (id: string) =>
  api<CompetitorDetail>(`/competition/competitors/${id}`);
export const createCompetitor = (input: {
  name: string;
  description?: string;
  website?: string;
}) =>
  api<CompetitorDetail>("/competition/competitors", {
    method: "POST",
    body: JSON.stringify(input),
  });
export const updateCompetitor = (
  id: string,
  fields: Partial<Pick<CompetitorDetail, "name" | "description" | "website">>,
) =>
  api<CompetitorDetail>(`/competition/competitors/${id}`, {
    method: "PATCH",
    body: JSON.stringify(fields),
  });
export const deleteCompetitor = (id: string) =>
  api<{ ok: boolean }>(`/competition/competitors/${id}`, { method: "DELETE" });

export async function uploadCompetitorLogo(
  competitorId: string,
  file: File,
): Promise<{ id: string; logoUrl: string }> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/competition/competitors/${competitorId}/logo`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const b = (await res.json()) as { message?: string };
      if (b?.message) message = b.message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json() as Promise<{ id: string; logoUrl: string }>;
}

export const deleteCompetitorLogo = (competitorId: string) =>
  api<{ id: string; logoUrl: null }>(`/competition/competitors/${competitorId}/logo`, {
    method: "DELETE",
  });

export const createCompetitorFolder = (
  competitorId: string,
  name: string,
  parentId?: string | null,
) =>
  api<CompetitorFolder>(`/competition/competitors/${competitorId}/folders`, {
    method: "POST",
    body: JSON.stringify(parentId ? { name, parentId } : { name }),
  });
export const renameCompetitorFolder = (id: string, name: string) =>
  api<CompetitorFolder>(`/competition/folders/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ name }),
  });
export const moveCompetitorFolder = (id: string, parentId: string | null) =>
  api<CompetitorFolder>(`/competition/folders/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ parentId }),
  });
export const deleteCompetitorFolder = (id: string) =>
  api<{ ok: boolean }>(`/competition/folders/${id}`, { method: "DELETE" });

export const moveCompetitorFile = (id: string, folderId: string | null) =>
  api<CompetitorFile>(`/competition/files/${id}/move`, {
    method: "PATCH",
    body: JSON.stringify({ folderId }),
  });

export async function uploadCompetitorFile(
  competitorId: string,
  file: File,
  folderId?: string | null,
): Promise<CompetitorFile> {
  const form = new FormData();
  form.append("file", file);
  const qs = folderId ? `?folderId=${encodeURIComponent(folderId)}` : "";
  const res = await fetch(`/api/competition/competitors/${competitorId}/files${qs}`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const b = (await res.json()) as { message?: string };
      if (b?.message) message = b.message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json() as Promise<CompetitorFile>;
}

export const competitorFileUrl = (id: string) => `/api/competition/files/${id}`;
export const competitorFilePreviewUrl = (id: string) =>
  `/api/competition/files/${id}/preview`;
export const deleteCompetitorFile = (id: string) =>
  api<{ ok: boolean }>(`/competition/files/${id}`, { method: "DELETE" });

// ── Finance ────────────────────────────────────────────────────────────
export type AccountType = "asset" | "liability" | "income" | "expense" | "equity";
export type TxDirection = "in" | "out";

export type CashflowSection = "operating" | "investing" | "financing";

export interface FinanceAccount {
  id: string;
  name: string;
  type: AccountType;
  code: string | null;
  cashflowSection: CashflowSection | null;
  archived: boolean;
  createdAt: string;
}

export interface FinanceTx {
  id: string;
  date: string;
  description: string;
  amount: number;
  direction: TxDirection;
  accountId: string;
  account: FinanceAccount;
  paidAccountId: string;
  paidAccount: FinanceAccount;
  reference: string | null;
  createdAt: string;
}

export interface PnlReport {
  income: { accountId: string; name: string; total: number }[];
  expense: { accountId: string; name: string; total: number }[];
  totalIncome: number;
  totalExpense: number;
  net: number;
}

export interface AccountBalance {
  id: string;
  name: string;
  type: AccountType;
  archived: boolean;
  balance: number;
}

export const listFinanceAccounts = () => api<FinanceAccount[]>("/finance/accounts");
export const createFinanceAccount = (input: {
  name: string;
  type: AccountType;
  code?: string;
}) => api<FinanceAccount>("/finance/accounts", { method: "POST", body: JSON.stringify(input) });
export const updateFinanceAccount = (
  id: string,
  fields: {
    name?: string;
    code?: string | null;
    cashflowSection?: CashflowSection | null;
    archived?: boolean;
  },
) =>
  api<FinanceAccount>(`/finance/accounts/${id}`, {
    method: "PATCH",
    body: JSON.stringify(fields),
  });

export const listTransactions = (filter: {
  from?: string;
  to?: string;
  accountId?: string;
}) => {
  const q = new URLSearchParams();
  if (filter.from) q.set("from", filter.from);
  if (filter.to) q.set("to", filter.to);
  if (filter.accountId) q.set("accountId", filter.accountId);
  const qs = q.toString();
  return api<FinanceTx[]>(`/finance/transactions${qs ? `?${qs}` : ""}`);
};
export const createTransaction = (input: {
  date: string;
  description: string;
  amount: number;
  direction: TxDirection;
  accountId: string;
  paidAccountId: string;
  reference?: string;
}) => api<FinanceTx>("/finance/transactions", { method: "POST", body: JSON.stringify(input) });
export const updateTransaction = (
  id: string,
  fields: Partial<{
    date: string;
    description: string;
    amount: number;
    direction: TxDirection;
    accountId: string;
    paidAccountId: string;
    reference: string | null;
  }>,
) =>
  api<FinanceTx>(`/finance/transactions/${id}`, {
    method: "PATCH",
    body: JSON.stringify(fields),
  });
export const deleteTransaction = (id: string) =>
  api<{ ok: boolean }>(`/finance/transactions/${id}`, { method: "DELETE" });

export interface ImportPreviewRow {
  date: string;
  description: string;
  amount: number;
  direction: TxDirection;
  importHash: string;
  suggestedAccountId: string | null;
  duplicate: boolean;
  likelyDuplicate: boolean;
}

export async function parseFinanceImport(
  file: File,
): Promise<{ rows: ImportPreviewRow[]; skipped: number; statementBalance: number | null }> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch("/api/finance/import/parse", {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    let message = `Import failed (${res.status})`;
    try {
      const b = (await res.json()) as { message?: string };
      if (b?.message) message = b.message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json() as Promise<{
    rows: ImportPreviewRow[];
    skipped: number;
    statementBalance: number | null;
  }>;
}

export const commitFinanceImport = (
  rows: {
    date: string;
    description: string;
    amount: number;
    direction: TxDirection;
    accountId: string;
    importHash: string;
  }[],
  paidAccountId: string,
) =>
  api<{ created: number; skippedDuplicates: number; paidAccountBalance: number }>(
    "/finance/import/commit",
    {
      method: "POST",
      body: JSON.stringify({ rows, paidAccountId }),
    },
  );

// ── Bank feed ──────────────────────────────────────────────────────────
export interface BankConnection {
  id: string;
  provider: string;
  status: "pending" | "linked" | "expired" | "error";
  accountName: string | null;
  consentExpiresAt: string | null;
  lastSyncedAt: string | null;
  lastSyncError: string | null;
}

export interface BankFeedStatus {
  provider: string;
  configured: boolean;
  connections: BankConnection[];
  pendingCount: number;
}

export interface BankFeedInboxItem {
  id: string;
  date: string;
  description: string;
  amount: number;
  direction: TxDirection;
  suggestedAccountId: string | null;
  accountName: string | null;
}

export const getBankFeedStatus = () => api<BankFeedStatus>("/bank-feed");
export const startBankLink = () =>
  api<{ url: string }>("/bank-feed/connections", { method: "POST" });
export const removeBankConnection = (id: string) =>
  api<{ ok: boolean }>(`/bank-feed/connections/${id}`, { method: "DELETE" });
export const syncBankFeed = () =>
  api<{ fetched: number; added: number }>("/bank-feed/sync", { method: "POST" });
export const getBankFeedInbox = () => api<BankFeedInboxItem[]>("/bank-feed/inbox");
export const approveBankFeedItems = (
  entries: { id: string; accountId: string }[],
  paidAccountId: string,
) =>
  api<{ created: number; skippedDuplicates: number }>("/bank-feed/inbox/approve", {
    method: "POST",
    body: JSON.stringify({ entries, paidAccountId }),
  });
export const dismissBankFeedItems = (ids: string[]) =>
  api<{ ok: boolean }>("/bank-feed/inbox/dismiss", {
    method: "POST",
    body: JSON.stringify({ ids }),
  });

// ── Statements ─────────────────────────────────────────────────────────
export interface MonthlyLine {
  accountId: string;
  name: string;
  monthly: number[];
  total: number;
}

export interface RecurringVendor {
  vendor: string;
  monthlyAvg: number;
  months: number;
  lastDate: string;
  lastAmount: number;
}

export interface Statements {
  year: number;
  pnl: {
    months: string[];
    income: MonthlyLine[];
    expense: MonthlyLine[];
    incomeMonthly: number[];
    expenseMonthly: number[];
    netMonthly: number[];
    totalIncome: number;
    totalExpense: number;
  };
  cashflow: {
    months: string[];
    operating: MonthlyLine[];
    investing: MonthlyLine[];
    financing: MonthlyLine[];
    operatingMonthly: number[];
    investingMonthly: number[];
    financingMonthly: number[];
    netMonthly: number[];
    opening: number;
    closingMonthly: number[];
  };
  balanceSheet: {
    cash: number;
    liabilities: number;
    contributed: number;
    retained: number;
    equityTotal: number;
    balances: boolean;
  };
  headline: { cash: number; burn: number; runwayMonths: number | null };
  recurring: RecurringVendor[];
  recurringTotal: number;
}

export const getStatements = (year?: number) =>
  api<Statements>(`/finance/reports/statements${year ? `?year=${year}` : ""}`);

export interface FinanceInsights {
  year: number;
  balanceSeries: { date: string; balance: number }[];
  monthly: { month: string; in: number; out: number; net: number }[];
  categories: { name: string; total: number }[];
}

export const getInsights = (year?: number) =>
  api<FinanceInsights>(`/finance/reports/insights${year ? `?year=${year}` : ""}`);

export const statementsXlsxUrl = (year: number) =>
  `/api/finance/export/statements.xlsx?year=${year}`;

export const getPnl = (from?: string, to?: string) => {
  const q = new URLSearchParams();
  if (from) q.set("from", from);
  if (to) q.set("to", to);
  const qs = q.toString();
  return api<PnlReport>(`/finance/reports/pnl${qs ? `?${qs}` : ""}`);
};
export const getBalances = () => api<AccountBalance[]>("/finance/reports/balances");
export const financeExportUrl = (from?: string, to?: string) => {
  const q = new URLSearchParams();
  if (from) q.set("from", from);
  if (to) q.set("to", to);
  const qs = q.toString();
  return `/api/finance/export${qs ? `?${qs}` : ""}`;
};

// ── Deployments ────────────────────────────────────────────────────────
export interface Deploy {
  id: string;
  status: string;
  inProgress: boolean;
  trigger: string | null;
  commitMessage: string | null;
  commitId: string | null;
  commitFullId: string | null;
  durationSecs: number | null;
  createdAt: string | null;
  finishedAt: string | null;
}

export interface ServiceLog {
  id: string;
  message: string;
  timestamp: string | null;
  labels: Record<string, string>;
}

export interface RenderService {
  id: string;
  name: string;
  type: string;
  repo: string | null;
  branch: string | null;
  autoDeploy: boolean;
  dashboardUrl: string | null;
  region: string | null;
  plan: string | null;
  env: string | null;
  url: string | null;
  suspended: boolean;
  updatedAt: string | null;
}

export interface ServiceSummary extends RenderService {
  latestDeploy: Deploy | null;
  hasNote: boolean;
  logoUrl: string | null;
  owner: CustomerOwner | null;
  /** What the service is within its deployment ("App", "Keycloak"…). */
  role: string | null;
  /** Whether its LLM usage is counted: it gets a Platform usage tab. */
  usage: boolean;
  /** Whether it's a PE app whose sign-ins are copied: it gets a Sessions tab. */
  sessions: boolean;
}

/** One deployment: the Render services that ship together, primary first. */
export interface DeploymentGroup {
  key: string;
  name: string;
  services: ServiceSummary[];
}

export const listDeploymentGroups = () =>
  api<{ configured: boolean; groups: DeploymentGroup[] }>("/deployments/groups");

export const getDeploymentService = (id: string) =>
  api<{
    service: RenderService;
    note: string | null;
    noteUpdatedAt: string | null;
    ownerId: string | null;
    owner: CustomerOwner | null;
    logoUrl: string | null;
    deploys: Deploy[];
  }>(`/deployments/services/${id}`);

export const setServiceOwner = (serviceId: string, ownerId: string | null) =>
  api<{ serviceId: string; ownerId: string | null }>(
    `/deployments/services/${serviceId}/meta`,
    { method: "PATCH", body: JSON.stringify({ ownerId }) },
  );

export async function uploadServiceLogo(
  serviceId: string,
  file: File,
): Promise<{ serviceId: string; logoUrl: string }> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/deployments/services/${serviceId}/logo`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const b = (await res.json()) as { message?: string };
      if (b?.message) message = b.message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json() as Promise<{ serviceId: string; logoUrl: string }>;
}

export const deleteServiceLogo = (serviceId: string) =>
  api<{ serviceId: string; logoUrl: null }>(`/deployments/services/${serviceId}/logo`, {
    method: "DELETE",
  });

export const getServiceLogs = (serviceId: string, limit = 100) =>
  api<ServiceLog[]>(`/deployments/services/${serviceId}/logs?limit=${limit}`);

export const triggerDeploy = (serviceId: string) =>
  api<Deploy>(`/deployments/services/${serviceId}/deploys`, { method: "POST" });

export const cancelDeploy = (serviceId: string, deployId: string) =>
  api<Deploy>(`/deployments/services/${serviceId}/deploys/${deployId}/cancel`, {
    method: "POST",
  });

export const restartService = (serviceId: string) =>
  api<{ ok: boolean }>(`/deployments/services/${serviceId}/restart`, { method: "POST" });

export const rollbackService = (serviceId: string, deployId: string) =>
  api<Deploy>(`/deployments/services/${serviceId}/rollback`, {
    method: "POST",
    body: JSON.stringify({ deployId }),
  });

// ── AI usage ───────────────────────────────────────────────────────────
// Mirrors backend/src/deployments/usage.service.ts.
export interface UsageTotals {
  /** Successful calls. */
  calls: number;
  /** Failed attempts, including ones a retry then fixed. */
  failed: number;
  inputTokens: number;
  outputTokens: number;
  /** A subset of inputTokens. */
  cachedTokens: number;
  totalTokens: number;
}

/** How far a service's logs have been read. */
export interface UsageCollection {
  collectedFrom: string;
  collectedTo: string;
  lastRunAt: string | null;
  lastRunLines: number;
  lastRunEvents: number;
  lastError: string | null;
}

export interface LlmUsage {
  configured: boolean;
  days?: number;
  /** Null until the service's logs are first read. */
  collection?: UsageCollection | null;
  totals?: UsageTotals;
  byAgent?: (UsageTotals & { agent: string })[];
  byModel?: (UsageTotals & { model: string })[];
  /** Tokens per agent per model. */
  byAgentModel?: Record<string, Record<string, number>>;
  byDay?: { day: string; totalTokens: number; byAgent: Record<string, number> }[];
}

export const getLlmUsage = (serviceId: string, days: number) =>
  api<LlmUsage>(`/deployments/usage?service=${encodeURIComponent(serviceId)}&days=${days}`);

export const collectLlmUsage = () =>
  api<{ ok: boolean }>("/deployments/usage/collect", { method: "POST" });

// ── PE sign-ins ────────────────────────────────────────────────────────
// Mirrors backend/src/deployments/sessions.ts.
export interface PeSessionView {
  id: string;
  /** "password", "sso" or "break-glass". */
  method: string;
  startedAt: string;
  /** The last token refresh: an open tab refreshes every ~15 minutes. */
  lastSeenAt: string;
  endedAt: string | null;
  endReason: string | null;
  /** online: a tab has it open · ended: the app ended it · closed: no tab has used it since lastSeenAt. */
  state: "online" | "ended" | "closed";
  /** Best known end; null while online. */
  end: string | null;
  durationMs: number;
}

export interface PeSessionUser {
  userId: string;
  name: string;
  email: string;
  role: string | null;
  isActive: boolean;
  online: boolean;
  lastSignInAt: string | null;
  sessions: PeSessionView[];
}

export interface PeSessions {
  configured: boolean;
  /** Null until the app's sessions are first copied. */
  sync?: {
    firstRunAt: string;
    lastRunAt: string | null;
    lastRunSessions: number;
    lastError: string | null;
  } | null;
  /** A-Z by name. */
  users?: PeSessionUser[];
}

export const getPeSessions = (serviceId: string) =>
  api<PeSessions>(`/deployments/sessions?service=${encodeURIComponent(serviceId)}`);

export const collectPeSessions = () =>
  api<{ ok: boolean }>("/deployments/sessions/collect", { method: "POST" });

// ── Env matrix ─────────────────────────────────────────────────────────
// Mirrors backend/src/env-matrix/env-matrix.types.ts. A secret cell's
// `value` is always null; secrets compare by fingerprint.
export type EnvCellSource =
  | "yaml"
  | "dashboard"
  | "generated"
  | "linked"
  | "group"
  | "untracked"
  | "dashboard_only"
  | "unknown";

export interface EnvMatrixCell {
  set: boolean;
  secret: boolean;
  value: string | null;
  fingerprint: string | null;
  revealable: boolean;
  source: EnvCellSource;
  line: number | null;
  fileUrl: string | null;
  group: string | null;
  overridesGroup: string | null;
  differsFromYaml: boolean;
  flag: "missing" | "bad" | null;
}

export interface EnvMatrixRow {
  key: string;
  kind: "var" | "file";
  group: string;
  /** The row's heading in each table that shows it, keyed by table name. */
  groups: Record<string, string>;
  status: "ok" | "fail" | null;
  cells: Record<string, EnvMatrixCell>;
}

export interface EnvMatrixService {
  id: string;
  name: string;
  type: string;
  environment: string;
  table: string;
  blueprint: string | null;
  dashboardUrl: string | null;
  suspended: boolean;
}

export interface EnvMatrixIssue {
  id: string;
  kind: "config" | "rule" | "notice";
  title: string;
  keys: string[];
  detail: string | null;
  fix: string | null;
  why: string | null;
  cells: { serviceId: string; key: string }[];
  /** For a rule: every cell it checks, broken or not. */
  targets: { serviceId: string; key: string }[];
}

export interface EnvMatrixCheck {
  label: string;
  keys: string[];
  status: "pass" | "n/a";
  detail: string | null;
  targets: { serviceId: string; key: string }[];
}

export interface EnvMatrix {
  configured: true;
  source: "render" | "fixture";
  collectedAt: string;
  environments: { name: string; serviceIds: string[] }[];
  /** Services worth comparing, side by side: one table per kind of service. */
  tables: { name: string; serviceIds: string[] }[];
  services: EnvMatrixService[];
  keyGroups: string[];
  rows: EnvMatrixRow[];
  issues: EnvMatrixIssue[];
  passing: EnvMatrixCheck[];
  /** Whether this viewer may reveal secrets (admins). */
  canReveal: boolean;
}

export type EnvMatrixResponse = EnvMatrix | { configured: false; missing: string[] };

export const getEnvMatrix = (refresh = false) =>
  api<EnvMatrixResponse>(`/env-matrix${refresh ? "?refresh=1" : ""}`);

export const ENV_MATRIX_CSV_URL = "/api/env-matrix/export.csv";

/** One secret's value. Admin-only, and every call is an audit row. */
export const revealEnvValue = (serviceId: string, key: string) =>
  api<{ value: string; fingerprint: string | null }>("/env-matrix/reveal", {
    method: "POST",
    body: JSON.stringify({ serviceId, key }),
  });

// ── Knowledge base / Chat ──────────────────────────────────────────────
export interface KnowledgeDoc {
  title: string;
  category: string;
  chunks: number;
  createdAt: string;
}
export interface ChatSource {
  n: number;
  title: string;
  /** "doc:<id>" / "attachment:<id>" — opens the original file. Null for chat uploads. */
  ref?: string | null;
  excerpts?: string[];
  /** Legacy shape (messages saved before sources were merged per document). */
  excerpt?: string;
}
export type ChatEvent =
  | { type: "sources"; sources: ChatSource[] }
  | { type: "status"; text: string }
  | { type: "token"; text: string }
  | { type: "done" }
  | { type: "error"; message: string };

export const listKnowledgeDocs = () => api<KnowledgeDoc[]>("/knowledge/documents");
export const deleteKnowledgeDoc = (title: string) =>
  api<{ deleted: number }>("/knowledge/documents/delete", {
    method: "POST",
    body: JSON.stringify({ title }),
  });

export async function uploadKnowledgeDoc(
  file: File,
): Promise<{ title: string; chunksCreated: number; embedded: number }> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch("/api/knowledge/documents/upload", {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const b = (await res.json()) as { message?: string };
      if (b?.message) message = b.message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json() as Promise<{ title: string; chunksCreated: number; embedded: number }>;
}

export interface ChatSessionMeta {
  id: string;
  title: string;
  updatedAt: string;
  createdAt: string;
}
export interface ChatMessageDto {
  role: "user" | "assistant";
  content: string;
  sources: ChatSource[] | null;
}

export const listChatSessions = () => api<ChatSessionMeta[]>("/knowledge/sessions");
export const createChatSession = () =>
  api<ChatSessionMeta>("/knowledge/sessions", { method: "POST" });
export const getChatSession = (id: string) =>
  api<{ id: string; title: string; messages: ChatMessageDto[] }>(`/knowledge/sessions/${id}`);
export const deleteChatSession = (id: string) =>
  api<{ ok: boolean }>(`/knowledge/sessions/${id}`, { method: "DELETE" });

/** Send a message in a session and stream the grounded answer (SSE). */
export async function streamSessionChat(
  sessionId: string,
  question: string,
  on: (e: ChatEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`/api/knowledge/sessions/${sessionId}/messages/stream`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ question }),
    signal,
  });
  if (!res.ok || !res.body) throw new ApiError(res.status, `Chat failed (${res.status})`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += decoder.decode(value, { stream: true });
    let i: number;
    while ((i = buf.indexOf("\n\n")) >= 0) {
      const frame = buf.slice(0, i);
      buf = buf.slice(i + 2);
      const line = frame.split("\n").find((l) => l.startsWith("data:"));
      if (!line) continue;
      const json = line.slice(5).trim();
      if (!json) continue;
      try {
        on(JSON.parse(json) as ChatEvent);
      } catch {
        /* skip malformed frame */
      }
    }
  }
}

// ── Document hub ───────────────────────────────────────────────────────
export interface DocFolder {
  id: string;
  parentId: string | null;
  name: string;
  createdAt: string;
  /** Live items directly inside — fills the folder row's Size cell. */
  itemCount: number;
  /** Newest change among its files, falling back to the folder's own createdAt. */
  updatedAt: string;
  starred: boolean;
}
export interface DocFile {
  id: string;
  folderId: string | null;
  name: string;
  mime: string;
  size: number;
  createdAt: string;
  updatedAt: string;
  lastEditedByName: string | null;
  /** Content requires the global document password. */
  isProtected: boolean;
  starred: boolean;
}
export interface DocFolderNode {
  id: string;
  name: string;
  parentId: string | null;
}
export interface DocVersion {
  id: string;
  name: string;
  size: number;
  createdAt: string;
  editedByName: string | null;
}
export interface DocsBrowse {
  folders: DocFolder[];
  documents: DocFile[];
  breadcrumb: { id: string; name: string }[];
}

export const browseDocs = (folderId: string | null) =>
  api<DocsBrowse>(`/docs/browse${folderId ? `?folderId=${folderId}` : ""}`);

// ── Search, recents, starred ───────────────────────────────────────────

/** A folder path, root-first, shown under each search / recents result. */
export type DocPath = { id: string; name: string }[];

export type DocSearchResult =
  | ({ kind: "folder"; path: DocPath; excerpt: null; matchedOn: "name" } & DocFolder)
  | ({
      kind: "file";
      path: DocPath;
      /** Matching text from the file's contents; null for name-only matches. */
      excerpt: string | null;
      matchedOn: "name" | "content" | "both";
    } & DocFile);

export interface DocSearchResponse {
  results: DocSearchResult[];
  /** False when the knowledge base returned nothing — results are names only. */
  contentSearch: boolean;
}

/** Hub-wide search: recursive filename match + file-content match. */
export const searchDocs = (q: string) =>
  api<DocSearchResponse>(`/docs/search?q=${encodeURIComponent(q)}`);

export type DocListItem = { kind: "file"; path: DocPath } & DocFile;

export const listRecentDocs = () => api<DocListItem[]>("/docs/recent");
export const listMyDocs = () => api<DocListItem[]>("/docs/mine");

export type DocStarredItem =
  | ({ kind: "folder"; path: DocPath } & DocFolder)
  | ({ kind: "file"; path: DocPath } & DocFile);

export const listStarredDocs = () => api<DocStarredItem[]>("/docs/starred");

/**
 * Paste-as-copy. Duplicates files/folders (recursively) into a destination
 * folder; colliding names get a " (n)" suffix rather than overwriting.
 */
export const copyDocItems = (
  items: { kind: "file" | "folder"; id: string }[],
  destFolderId: string | null,
) =>
  api<{ kind: "file" | "folder"; id: string; name: string }[]>("/docs/copy", {
    method: "POST",
    body: JSON.stringify({ items, destFolderId }),
  });

export const setDocStar = (
  targetType: "file" | "folder",
  targetId: string,
  starred: boolean,
) =>
  api<{ starred: boolean }>("/docs/stars", {
    method: "POST",
    body: JSON.stringify({ targetType, targetId, starred }),
  });

/** Which folder a file lives in — resolves a ?file= deep link. */
export const locateDocFile = (id: string) =>
  api<{ folderId: string | null; breadcrumb: DocPath }>(`/docs/files/${id}/locate`);

// ── Trash ──────────────────────────────────────────────────────────────

export interface DocTrashEntry {
  kind: "file" | "folder";
  id: string;
  name: string;
  size: number | null;
  trashedAt: string;
  trashedByName: string | null;
  /** When the daily sweep will destroy it for good. */
  purgesAt: string;
  /** For folders: how many items were deleted alongside it. */
  itemCount: number;
}

export const listDocTrash = () => api<DocTrashEntry[]>("/docs/trash");

export const restoreDocTrash = (id: string) =>
  api<{ ok: boolean }>(`/docs/trash/${id}/restore`, { method: "POST" });

/** Destroy one trash entry for good. */
export const purgeDocTrash = (id: string) =>
  api<{ ok: boolean }>(`/docs/trash/${id}`, { method: "DELETE" });

export const emptyDocTrash = () =>
  api<{ purged: number }>("/docs/trash", { method: "DELETE" });

export const createDocFolder = (name: string, parentId: string | null) =>
  api<DocFolder>("/docs/folders", {
    method: "POST",
    body: JSON.stringify({ name, ...(parentId ? { parentId } : {}) }),
  });

export const updateDocFolder = (
  id: string,
  fields: { name?: string; parentId?: string | null },
) => api<DocFolder>(`/docs/folders/${id}`, { method: "PATCH", body: JSON.stringify(fields) });

export const deleteDocFolder = (id: string) =>
  api<{ ok: boolean }>(`/docs/folders/${id}`, { method: "DELETE" });

const unlockQs = (unlock?: string | null) =>
  unlock ? `?unlock=${encodeURIComponent(unlock)}` : "";

/** Inline-view / download URL for a stored file. */
export const docFileUrl = (id: string, unlock?: string | null) =>
  `/api/docs/files/${id}/content${unlockQs(unlock)}`;

/** Server HTML-conversion URL (docx / xlsx / csv / pptx previews). */
export const docPreviewUrl = (id: string, unlock?: string | null) =>
  `/api/docs/files/${id}/preview${unlockQs(unlock)}`;

// ── Document protection (per-file passwords, Excel-style) ──────────────
const unlockStoreKey = (id: string) => `docs-unlock:${id}`;

/** Exchange a file's password for an unlock token, cached per tab session. */
export const unlockDoc = async (id: string, password: string) => {
  const r = await api<{ token: string; expiresAt: number }>(
    `/docs/files/${id}/unlock`,
    { method: "POST", body: JSON.stringify({ password }) },
  );
  sessionStorage.setItem(unlockStoreKey(id), JSON.stringify(r));
  return r;
};

/** Valid cached unlock token for this file in this tab session, or null. */
export const getDocUnlockToken = (id: string): string | null => {
  try {
    const raw = sessionStorage.getItem(unlockStoreKey(id));
    if (!raw) return null;
    const { token, expiresAt } = JSON.parse(raw) as {
      token: string;
      expiresAt: number;
    };
    // A minute of slack so a token can't expire mid-interaction.
    if (Date.now() > expiresAt - 60_000) return null;
    return token;
  } catch {
    return null;
  }
};

export type DocEditContent =
  | { name: string; kind: "text"; content: string }
  | { name: string; kind: "html"; html: string }
  | {
      name: string;
      kind: "sheets";
      sheets: { name: string; rows: (string | number | null)[][] }[];
    };

export type DocSavePayload =
  | { kind: "text"; content: string }
  | { kind: "html"; html: string }
  | { kind: "sheets"; sheets: { name: string; rows: (string | number | null)[][] }[] };

export const getDocEditContent = (id: string) =>
  api<DocEditContent>(`/docs/files/${id}/edit${unlockQs(getDocUnlockToken(id))}`);

export const saveDocContent = (id: string, payload: DocSavePayload) =>
  api<DocFile>(`/docs/files/${id}/content`, {
    method: "PUT",
    body: JSON.stringify({ ...payload, unlock: getDocUnlockToken(id) ?? undefined }),
  });

/** Types the in-browser editor supports (xlsm excluded — macros would be lost). */
export const canEditDoc = (name: string): boolean =>
  /\.(md|txt|csv|json|log|ya?ml|xml|docx|xlsx|xls|pptx)$/i.test(name) &&
  !/\.xlsm$/i.test(name);

export type OfficeEditorKind = "gridcraft" | "wordcraft" | "deckcraft";

/** The embedded editor for a file, or null when the plain editor handles it. */
export const officeEditorFor = (name: string): OfficeEditorKind | null =>
  /\.xlsx$/i.test(name)
    ? "gridcraft"
    : /\.docx$/i.test(name)
      ? "wordcraft"
      : /\.pptx$/i.test(name)
        ? "deckcraft"
        : null;

/** The file's bytes plus the revision a later save must be based on. */
export async function loadOfficeFile(
  id: string,
): Promise<{ bytes: ArrayBuffer; revision: string }> {
  const res = await fetch(`/api/docs/files/${id}/content${unlockQs(getDocUnlockToken(id))}`, {
    credentials: "include",
  });
  if (!res.ok) throw new ApiError(res.status, `Could not open this file (${res.status})`);
  const revision = res.headers.get("X-Doc-Revision");
  if (!revision) throw new ApiError(500, "The server did not report the file's revision");
  return { bytes: await res.arrayBuffer(), revision };
}

/** Save a whole exported file. Rejects with 409 if the file changed since `baseRevision`. */
export async function saveOfficeFile(
  id: string,
  bytes: ArrayBuffer,
  name: string,
  mime: string,
  baseRevision: string,
): Promise<{ revision: string; size: number }> {
  const form = new FormData();
  form.append("baseRevision", baseRevision);
  const unlock = getDocUnlockToken(id);
  if (unlock) form.append("unlock", unlock);
  form.append("file", new File([bytes], name, { type: mime }));
  const res = await fetch(`/api/docs/files/${id}/office`, {
    method: "PUT",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    let message = `Save failed (${res.status})`;
    try {
      const b = (await res.json()) as { message?: string };
      if (b?.message) message = b.message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json() as Promise<{ revision: string; size: number }>;
}

export const updateDocFile = (
  id: string,
  fields: {
    name?: string;
    folderId?: string | null;
    isProtected?: boolean;
    password?: string;
    unlock?: string;
  },
) => api<DocFile>(`/docs/files/${id}`, { method: "PATCH", body: JSON.stringify(fields) });

export const deleteDocFile = (id: string) =>
  api<{ ok: boolean }>(`/docs/files/${id}`, { method: "DELETE" });

/** Upload into a folder; relPath ("sub/dir") auto-creates folders beneath it. */
export async function uploadDocFile(
  file: File,
  folderId: string | null,
  relPath?: string,
): Promise<DocFile> {
  const form = new FormData();
  form.append("file", file);
  if (folderId) form.append("folderId", folderId);
  if (relPath) form.append("relPath", relPath);
  const res = await fetch("/api/docs/files", {
    method: "POST",
    credentials: "include",
    body: form, // no Content-Type — browser sets multipart boundary
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const b = (await res.json()) as { message?: string };
      if (b?.message) message = b.message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json() as Promise<DocFile>;
}

/** Overwrite an existing file's bytes in place (keeps its id + version history). */
export async function replaceDocFile(id: string, file: File): Promise<DocFile> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/docs/files/${id}/replace`, {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) throw new ApiError(res.status, `Replace failed (${res.status})`);
  return res.json() as Promise<DocFile>;
}

/** Flat list of every folder — for the move-to picker. */
export const getDocFolderTree = () => api<DocFolderNode[]>("/docs/folders/tree");

/** Prior versions of a file, newest first. */
export const listDocVersions = (id: string) =>
  api<DocVersion[]>(`/docs/files/${id}/versions${unlockQs(getDocUnlockToken(id))}`);

/** Restore a file to a prior version. */
export const restoreDocVersion = (id: string, versionId: string) =>
  api<DocFile>(
    `/docs/files/${id}/versions/${versionId}/restore${unlockQs(getDocUnlockToken(id))}`,
    { method: "POST" },
  );

/** Download URL for a specific version's bytes. */
export const docVersionUrl = (id: string, versionId: string) =>
  `/api/docs/files/${id}/versions/${versionId}/content${unlockQs(getDocUnlockToken(id))}`;

/** Download URL for a whole folder as a zip. */
export const folderZipUrl = (id: string) => `/api/docs/folders/${id}/zip`;

/** POST a set of file ids and download them as a single zip. */
export async function downloadDocsZip(ids: string[]): Promise<void> {
  const res = await fetch("/api/docs/zip", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids }),
  });
  if (!res.ok) throw new ApiError(res.status, `Download failed (${res.status})`);
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "documents.zip";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export async function uploadAttachment(ticketId: string, file: File): Promise<Attachment> {
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`/api/board/tickets/${ticketId}/attachments`, {
    method: "POST",
    credentials: "include",
    body: form, // no Content-Type — browser sets multipart boundary
  });
  if (!res.ok) throw new ApiError(res.status, `Upload failed (${res.status})`);
  return res.json() as Promise<Attachment>;
}

// ── Compliance ─────────────────────────────────────────────────────────

export type FrameworkKey = "iso27001" | "soc2";
export type ControlFrequency =
  | "continuous"
  | "daily"
  | "weekly"
  | "monthly"
  | "quarterly"
  | "annual"
  | "event_driven";
export type Applicability = "undecided" | "applicable" | "not_applicable";
export type ControlStatus = "draft" | "defined" | "approved" | "stale";
export type AttestationAction = "prepared" | "reviewed" | "approved";
export type CycleStage = "none" | AttestationAction;

export interface ComplianceFramework {
  id: string;
  key: FrameworkKey;
  name: string;
  version: string;
  description: string | null;
}

export interface ComplianceRequirement {
  id: string;
  frameworkId: string;
  parentId: string | null;
  ref: string;
  title: string;
  kind: "group" | "clause" | "control" | "criterion";
  summary: string | null;
  sortOrder: number;
  sourceStatus: "provisional" | "verified";
  verifiedById: string | null;
  verifiedAt: string | null;
}

export interface ComplianceControl {
  id: string;
  ref: string;
  name: string;
  description: string;
  category: string;
  ownerId: string | null;
  owner?: TeamUser | null;
  frequency: ControlFrequency;
  testProcedure: string;
  applicability: Applicability;
  applicabilityJustification: string | null;
  sourceStatus: "provisional" | "verified";
  auditorNotes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ControlBlocker {
  rule: string;
  message: string;
}

export interface ControlMapping {
  requirementId: string;
  frameworkKey: FrameworkKey;
  ref: string;
  title: string;
  note: string | null;
}

export interface ControlView {
  control: ComplianceControl;
  status: ControlStatus;
  cycleStage: CycleStage;
  blockers: ControlBlocker[];
  mappings: ControlMapping[];
}

export interface ComplianceAttestation {
  id: string;
  objectType: string;
  objectId: string;
  action: AttestationAction;
  actorId: string;
  actor?: TeamUser;
  statement: string;
  contentHash: string;
  prevHash: string | null;
  chainHash: string;
  occurredAt: string;
  recordedAt: string;
}

export interface SodExceptionRecord {
  id: string;
  objectType: string;
  objectId: string;
  rule: string;
  requestedById: string;
  requestedBy?: TeamUser;
  justification: string;
  compensatingControl: string;
  status: "pending" | "approved" | "rejected";
  decidedById: string | null;
  decidedBy?: TeamUser | null;
  decisionNote: string | null;
  decidedAt: string | null;
  createdAt: string;
}

export interface ComplianceOverview {
  controls: {
    total: number;
    applicable: number;
    excluded: number;
    clean: number;
    byStatus: Record<string, number>;
  };
  frameworks: {
    key: FrameworkKey;
    name: string;
    version: string;
    requirementCount: number;
    coveredCount: number;
    uncovered: { id: string; ref: string; title: string }[];
  }[];
  blockers: (ControlBlocker & {
    kind:
      | "control"
      | "policy"
      | "occurrence"
      | "risk"
      | "soa"
      | "person"
      | "vendor"
      | "finding"
      | "audit";
    objectId: string;
    refLabel: string;
    name: string;
    severity?: Severity;
    area?: string;
  })[];
  readiness?: FrameworkReadiness[];
  provisional: { requirements: number; controls: number };
  policies?: { total: number; published: number; lapsed: number; fullyAcknowledged: number };
  operating?: {
    periodId: string;
    periodName: string;
    total: number;
    closed: number;
    overdue: number;
    exceptions: number;
  } | null;
  risks?: { total: number; accepted: number; aboveAppetite: number };
  soa?: {
    total: number;
    applicable: number;
    excluded: number;
    unresolved: number;
    releasedVersion: number | null;
  };
  people?: { total: number; active: number; leavers: number; clean: number };
  vendors?: { total: number; critical: number; clean: number; assuranceLapsing: number };
  audit?: {
    openFindings: number;
    openMajor: number;
    awaitingVerification: number;
    programmes: number;
  };
}

export type Severity = "critical" | "high" | "medium";
export type RequirementState = "ready" | "needs_work" | "not_implemented";

export interface RequirementReadiness {
  requirementId: string;
  ref: string;
  title: string;
  state: RequirementState;
  controlRefs: string[];
  blockers: (ControlBlocker & { severity: Severity })[];
}

export interface FrameworkReadiness {
  key: FrameworkKey;
  name: string;
  version: string;
  total: number;
  ready: number;
  needsWork: number;
  notImplemented: number;
  percent: number;
  requirements: RequirementReadiness[];
}

export const getFrameworkReadiness = () => api<FrameworkReadiness[]>("/compliance/readiness");

export const getComplianceOverview = () => api<ComplianceOverview>("/compliance/overview");

export const listComplianceFrameworks = () => api<ComplianceFramework[]>("/compliance/frameworks");

export const listFrameworkRequirements = (key: FrameworkKey) =>
  api<{ requirements: ComplianceRequirement[]; coverage: Record<string, number> }>(
    `/compliance/frameworks/${key}/requirements`,
  );

export const listComplianceControls = () => api<ControlView[]>("/compliance/controls");

export const getComplianceControl = (id: string) => api<ControlView>(`/compliance/controls/${id}`);

export const updateComplianceControl = (
  id: string,
  input: Partial<
    Pick<
      ComplianceControl,
      | "name"
      | "description"
      | "category"
      | "ownerId"
      | "frequency"
      | "testProcedure"
      | "applicability"
      | "applicabilityJustification"
      | "auditorNotes"
    >
  >,
) => api<ControlView>(`/compliance/controls/${id}`, { method: "PATCH", body: JSON.stringify(input) });

export const listControlAttestations = (id: string) =>
  api<{
    attestations: ComplianceAttestation[];
    chain: { valid: boolean; length: number; brokenAtId: string | null };
  }>(`/compliance/controls/${id}/attestations`);

export const attestControl = (id: string, action: AttestationAction, statement: string) =>
  api<ControlView>(`/compliance/controls/${id}/attest`, {
    method: "POST",
    body: JSON.stringify({ action, statement }),
  });

export const listSodExceptions = () => api<SodExceptionRecord[]>("/compliance/sod-exceptions");

export const requestSodException = (
  controlId: string,
  justification: string,
  compensatingControl: string,
) =>
  api<SodExceptionRecord>(`/compliance/controls/${controlId}/sod-exceptions`, {
    method: "POST",
    body: JSON.stringify({ justification, compensatingControl }),
  });

export const decideSodException = (id: string, approve: boolean, note?: string) =>
  api<SodExceptionRecord>(`/compliance/sod-exceptions/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ approve, note }),
  });

export const verifyComplianceRequirement = (id: string, summary?: string) =>
  api<ComplianceRequirement>(`/compliance/requirements/${id}/verify`, {
    method: "PATCH",
    body: JSON.stringify({ summary }),
  });

// ── Compliance: policies (phase 3) ─────────────────────────────────────

export type PolicyKind = "policy" | "standard" | "procedure";
export type PolicyStatus = "draft" | "in_review" | "approved" | "published" | "lapsed";

export interface CompliancePolicy {
  id: string;
  name: string;
  kind: PolicyKind;
  ownerId: string | null;
  owner?: TeamUser | null;
  reviewCadenceMonths: number;
  createdAt: string;
  updatedAt: string;
}

export interface PolicyVersionRecord {
  id: string;
  policyId: string;
  version: number;
  body: string;
  createdById: string | null;
  createdBy?: TeamUser | null;
  publishedAt: string | null;
  publishedById: string | null;
  effectiveFrom: string | null;
  supersededAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PolicyView {
  policy: CompliancePolicy;
  status: PolicyStatus;
  blockers: ControlBlocker[];
  currentVersion: PolicyVersionRecord | null;
  latestVersion: PolicyVersionRecord | null;
  latestCycleStage: CycleStage;
  ackCount: number;
  activeUserCount: number;
  acknowledgedByMe: boolean;
  linkedControlIds: string[];
}

export interface PolicyAckRecord {
  id: string;
  policyVersionId: string;
  userId: string;
  user?: TeamUser;
  acknowledgedAt: string;
}

export const listCompliancePolicies = () => api<PolicyView[]>("/compliance/policies");

export const getCompliancePolicy = (id: string) => api<PolicyView>(`/compliance/policies/${id}`);

export const createCompliancePolicy = (input: {
  name: string;
  kind?: PolicyKind;
  ownerId?: string | null;
  reviewCadenceMonths?: number;
}) => api<PolicyView>("/compliance/policies", { method: "POST", body: JSON.stringify(input) });

export const updateCompliancePolicy = (
  id: string,
  input: Partial<Pick<CompliancePolicy, "name" | "kind" | "ownerId" | "reviewCadenceMonths">>,
) => api<PolicyView>(`/compliance/policies/${id}`, { method: "PATCH", body: JSON.stringify(input) });

export const listPolicyVersions = (policyId: string) =>
  api<PolicyVersionRecord[]>(`/compliance/policies/${policyId}/versions`);

export const createPolicyVersion = (policyId: string, body: string) =>
  api<PolicyVersionRecord>(`/compliance/policies/${policyId}/versions`, {
    method: "POST",
    body: JSON.stringify({ body }),
  });

export const editPolicyVersion = (versionId: string, body: string) =>
  api<PolicyVersionRecord>(`/compliance/policies/versions/${versionId}`, {
    method: "PATCH",
    body: JSON.stringify({ body }),
  });

export const publishPolicyVersion = (versionId: string, effectiveFrom?: string) =>
  api<PolicyVersionRecord>(`/compliance/policies/versions/${versionId}/publish`, {
    method: "POST",
    body: JSON.stringify({ effectiveFrom }),
  });

export const acknowledgePolicyVersion = (versionId: string) =>
  api<PolicyAckRecord>(`/compliance/policies/versions/${versionId}/acknowledge`, { method: "POST" });

export const listPolicyAcks = (versionId: string) =>
  api<PolicyAckRecord[]>(`/compliance/policies/versions/${versionId}/acknowledgements`);

export const listPolicyVersionAttestations = (versionId: string) =>
  api<{
    attestations: ComplianceAttestation[];
    chain: { valid: boolean; length: number; brokenAtId: string | null };
  }>(`/compliance/policies/versions/${versionId}/attestations`);

export const attestPolicyVersion = (versionId: string, action: AttestationAction, statement: string) =>
  api<PolicyView>(`/compliance/policies/versions/${versionId}/attest`, {
    method: "POST",
    body: JSON.stringify({ action, statement }),
  });

export const linkPolicyControl = (policyId: string, controlId: string) =>
  api<PolicyView>(`/compliance/policies/${policyId}/controls`, {
    method: "POST",
    body: JSON.stringify({ controlId }),
  });

export const unlinkPolicyControl = (policyId: string, controlId: string) =>
  api<PolicyView>(`/compliance/policies/${policyId}/controls/${controlId}`, { method: "DELETE" });

// ── Compliance: operating record (phase 4) ─────────────────────────────

export type OccurrenceResult = "pending" | "effective" | "exception";
export type OccurrenceStage = "open" | "prepared" | "closed";

export interface CompliancePeriod {
  id: string;
  name: string;
  startsOn: string;
  endsOn: string;
  description: string | null;
  createdAt: string;
}

export interface ComplianceOccurrence {
  id: string;
  controlId: string;
  control?: ComplianceControl & { owner?: TeamUser | null };
  periodId: string;
  period?: CompliancePeriod;
  dueOn: string;
  occurredOn: string | null;
  result: OccurrenceResult;
  populationSize: number | null;
  sampleSize: number | null;
  deviations: number | null;
  notes: string | null;
}

export interface EvidenceRecord {
  id: string;
  name: string;
  description: string | null;
  mime: string;
  size: number;
  sha256: string;
  capturedOn: string | null;
  uploadedById: string | null;
  uploadedBy?: TeamUser | null;
  createdAt: string;
}

export interface OccurrenceView {
  occurrence: ComplianceOccurrence;
  stage: OccurrenceStage;
  blockers: ControlBlocker[];
  evidence: { link: { id: string; note: string | null }; evidence: EvidenceRecord }[];
}

export interface TimelineCell {
  id: string;
  dueOn: string;
  occurredOn: string | null;
  result: string;
  stage: OccurrenceStage;
  overdue: boolean;
  exception: boolean;
  evidenceCount: number;
}

export interface PeriodTimeline {
  period: CompliancePeriod;
  rows: {
    controlId: string;
    ref: string;
    name: string;
    frequency: ControlFrequency;
    owner: { id: string; name: string } | null;
    cells: TimelineCell[];
  }[];
}

export const listCompliancePeriods = () => api<CompliancePeriod[]>("/compliance/periods");

export const createCompliancePeriod = (input: {
  name: string;
  startsOn: string;
  endsOn: string;
  description?: string;
}) => api<CompliancePeriod>("/compliance/periods", { method: "POST", body: JSON.stringify(input) });

export const generateOccurrences = (periodId: string) =>
  api<{ created: number }>(`/compliance/periods/${periodId}/generate`, { method: "POST" });

export const getPeriodTimeline = (periodId: string) =>
  api<PeriodTimeline>(`/compliance/periods/${periodId}/timeline`);

export const getOccurrence = (id: string) => api<OccurrenceView>(`/compliance/occurrences/${id}`);

export const updateOccurrence = (
  id: string,
  input: Partial<
    Pick<
      ComplianceOccurrence,
      "occurredOn" | "result" | "populationSize" | "sampleSize" | "deviations" | "notes"
    >
  >,
) => api<OccurrenceView>(`/compliance/occurrences/${id}`, { method: "PATCH", body: JSON.stringify(input) });

export const attestOccurrence = (id: string, action: AttestationAction, statement: string) =>
  api<OccurrenceView>(`/compliance/occurrences/${id}/attest`, {
    method: "POST",
    body: JSON.stringify({ action, statement }),
  });

export const linkOccurrenceEvidence = (id: string, evidenceId: string, note?: string) =>
  api<OccurrenceView>(`/compliance/occurrences/${id}/evidence`, {
    method: "POST",
    body: JSON.stringify({ evidenceId, note }),
  });

export const unlinkOccurrenceEvidence = (id: string, evidenceId: string) =>
  api<OccurrenceView>(`/compliance/occurrences/${id}/evidence/${evidenceId}`, { method: "DELETE" });

export const listComplianceEvidence = () => api<EvidenceRecord[]>("/compliance/evidence");

export async function uploadComplianceEvidence(input: {
  file: File;
  name?: string;
  capturedOn?: string;
  occurrenceId?: string;
}): Promise<EvidenceRecord> {
  const form = new FormData();
  form.append("file", input.file);
  if (input.name) form.append("name", input.name);
  if (input.capturedOn) form.append("capturedOn", input.capturedOn);
  if (input.occurrenceId) form.append("occurrenceId", input.occurrenceId);
  const res = await fetch("/api/compliance/evidence", {
    method: "POST",
    credentials: "include",
    body: form, // no Content-Type — browser sets multipart boundary
  });
  if (!res.ok) {
    let message = `Upload failed (${res.status})`;
    try {
      const b = (await res.json()) as { message?: string };
      if (b?.message) message = b.message;
    } catch {
      /* ignore */
    }
    throw new ApiError(res.status, message);
  }
  return res.json() as Promise<EvidenceRecord>;
}

// ── Compliance: risk + SoA (phase 5) ───────────────────────────────────

export type TreatmentOption = "modify" | "retain" | "avoid" | "share";
export type RiskStatus = "identified" | "assessed" | "treated" | "accepted" | "closed";
export type SoaApplicability = "undecided" | "applicable" | "not_applicable";

export interface RiskMethodology {
  id: string;
  likelihoodScale: string[];
  impactScale: string[];
  appetiteThreshold: number;
  reviewCadenceMonths: number;
  notes: string | null;
}

export interface ComplianceRisk {
  id: string;
  ref: string;
  title: string;
  description: string;
  category: string;
  ownerId: string | null;
  owner?: TeamUser | null;
  inherentLikelihood: number | null;
  inherentImpact: number | null;
  treatmentOption: TreatmentOption | null;
  treatmentPlan: string | null;
  residualLikelihood: number | null;
  residualImpact: number | null;
  reviewCadenceMonths: number | null;
  lastReviewedAt: string | null;
  closedAt: string | null;
  closureReason: string | null;
}

export interface RiskView {
  risk: ComplianceRisk;
  status: RiskStatus;
  blockers: ControlBlocker[];
  inherentScore: number | null;
  residualScore: number | null;
  cycleStage: CycleStage;
  controls: { id: string; ref: string; name: string }[];
}

export interface RiskReviewRecord {
  id: string;
  riskId: string;
  reviewedById: string;
  reviewedBy?: TeamUser;
  residualScore: number | null;
  note: string;
  reviewedAt: string;
}

export interface SoaEntryView {
  entry: { id: string; requirementId: string; applicability: SoaApplicability; justification: string | null };
  ref: string;
  title: string;
  blockers: ControlBlocker[];
  controls: { id: string; ref: string; name: string; status: string }[];
  riskRefs: string[];
}

export interface SoaVersionRecord {
  id: string;
  version: number;
  entries: unknown[];
  generatedById: string | null;
  publishedAt: string | null;
  publishedById: string | null;
  createdAt: string;
}

export const getRiskMethodology = () => api<RiskMethodology>("/compliance/risk-methodology");

export const updateRiskMethodology = (input: {
  appetiteThreshold?: number;
  reviewCadenceMonths?: number;
  notes?: string | null;
}) => api<RiskMethodology>("/compliance/risk-methodology", { method: "PATCH", body: JSON.stringify(input) });

export const listRisks = () => api<RiskView[]>("/compliance/risks");

export const getRisk = (id: string) => api<RiskView>(`/compliance/risks/${id}`);

export const createRisk = (input: {
  title: string;
  description: string;
  category: string;
  ownerId?: string | null;
}) => api<RiskView>("/compliance/risks", { method: "POST", body: JSON.stringify(input) });

export const updateRisk = (
  id: string,
  input: Partial<
    Pick<
      ComplianceRisk,
      | "title"
      | "description"
      | "category"
      | "ownerId"
      | "inherentLikelihood"
      | "inherentImpact"
      | "treatmentOption"
      | "treatmentPlan"
      | "residualLikelihood"
      | "residualImpact"
    >
  >,
) => api<RiskView>(`/compliance/risks/${id}`, { method: "PATCH", body: JSON.stringify(input) });

export const closeRisk = (id: string, reason: string) =>
  api<RiskView>(`/compliance/risks/${id}/close`, { method: "POST", body: JSON.stringify({ reason }) });

export const attestRisk = (id: string, action: AttestationAction, statement: string) =>
  api<RiskView>(`/compliance/risks/${id}/attest`, {
    method: "POST",
    body: JSON.stringify({ action, statement }),
  });

export const linkRiskControl = (id: string, controlId: string) =>
  api<RiskView>(`/compliance/risks/${id}/controls`, {
    method: "POST",
    body: JSON.stringify({ controlId }),
  });

export const unlinkRiskControl = (id: string, controlId: string) =>
  api<RiskView>(`/compliance/risks/${id}/controls/${controlId}`, { method: "DELETE" });

export const listRiskReviews = (id: string) => api<RiskReviewRecord[]>(`/compliance/risks/${id}/reviews`);

export const addRiskReview = (id: string, note: string) =>
  api<RiskReviewRecord>(`/compliance/risks/${id}/reviews`, {
    method: "POST",
    body: JSON.stringify({ note }),
  });

export const listSoaEntries = () => api<SoaEntryView[]>("/compliance/soa");

export const updateSoaEntry = (
  id: string,
  input: { applicability?: SoaApplicability; justification?: string | null },
) => api<SoaEntryView>(`/compliance/soa/entries/${id}`, { method: "PATCH", body: JSON.stringify(input) });

export const listSoaVersions = () => api<SoaVersionRecord[]>("/compliance/soa/versions");

export const generateSoaVersion = () =>
  api<SoaVersionRecord>("/compliance/soa/versions", { method: "POST" });

export const getSoaVersion = (id: string) =>
  api<{ version: SoaVersionRecord; cycleStage: CycleStage; attestations: ComplianceAttestation[] }>(
    `/compliance/soa/versions/${id}`,
  );

export const attestSoaVersion = (id: string, action: AttestationAction, statement: string) =>
  api<SoaVersionRecord>(`/compliance/soa/versions/${id}/attest`, {
    method: "POST",
    body: JSON.stringify({ action, statement }),
  });

export const publishSoaVersion = (id: string) =>
  api<SoaVersionRecord>(`/compliance/soa/versions/${id}/publish`, { method: "POST" });

// ── Compliance: people + vendors (phase 6) ─────────────────────────────

export type PersonType = "employee" | "contractor" | "founder";
export type PersonStatus = "pending" | "active" | "leaver";
export type CheckKind = "onboarding" | "offboarding";
export type VendorCriticality = "critical" | "important" | "low";
export type DataClass = "none" | "internal" | "personal" | "confidential";
export type AssuranceType = "none" | "soc2" | "iso27001" | "both" | "other";
export type ReviewOutcome = "satisfactory" | "concerns" | "terminate";

export interface CompliancePerson {
  id: string;
  name: string;
  email: string | null;
  type: PersonType;
  role: string | null;
  userId: string | null;
  startedOn: string | null;
  endedOn: string | null;
  notes: string | null;
}

export interface PersonCheckRecord {
  id: string;
  personId: string;
  kind: CheckKind;
  key: string;
  label: string;
  controlRef: string | null;
  completedOn: string | null;
  completedById: string | null;
  completedBy?: TeamUser | null;
  note: string | null;
  sortOrder: number;
}

export interface TrainingRecordItem {
  id: string;
  personId: string;
  topic: string;
  completedOn: string;
  evidenceId: string | null;
}

export interface PersonView {
  person: CompliancePerson;
  status: PersonStatus;
  blockers: ControlBlocker[];
  checks: PersonCheckRecord[];
  training: TrainingRecordItem[];
  lastTrainingOn: string | null;
}

export interface ComplianceVendor {
  id: string;
  name: string;
  purpose: string;
  criticality: VendorCriticality;
  dataClass: DataClass;
  ownerId: string | null;
  owner?: TeamUser | null;
  website: string | null;
  assurance: AssuranceType;
  assuranceExpiresOn: string | null;
  dpaOnFile: boolean;
  subProcessors: string | null;
  reviewCadenceMonths: number;
  lastReviewedOn: string | null;
  retiredOn: string | null;
  notes: string | null;
}

export interface VendorReviewRecord {
  id: string;
  vendorId: string;
  reviewedById: string;
  reviewedBy?: TeamUser;
  reviewedOn: string;
  outcome: ReviewOutcome;
  findings: string;
  evidenceId: string | null;
}

export interface VendorView {
  vendor: ComplianceVendor;
  blockers: ControlBlocker[];
  reviews: VendorReviewRecord[];
  retired: boolean;
}

export const listCompliancePeople = () => api<PersonView[]>("/compliance/people");

export const createCompliancePerson = (input: {
  name: string;
  type?: PersonType;
  email?: string | null;
  role?: string | null;
  startedOn?: string | null;
}) => api<PersonView>("/compliance/people", { method: "POST", body: JSON.stringify(input) });

export const updateCompliancePerson = (
  id: string,
  input: Partial<Pick<CompliancePerson, "name" | "type" | "email" | "role" | "userId" | "startedOn" | "endedOn" | "notes">>,
) => api<PersonView>(`/compliance/people/${id}`, { method: "PATCH", body: JSON.stringify(input) });

export const setPersonCheck = (checkId: string, completedOn: string | null, note?: string) =>
  api<PersonView>(`/compliance/people/checks/${checkId}`, {
    method: "PATCH",
    body: JSON.stringify({ completedOn, note }),
  });

export const addPersonTraining = (id: string, topic: string, completedOn: string) =>
  api<PersonView>(`/compliance/people/${id}/training`, {
    method: "POST",
    body: JSON.stringify({ topic, completedOn }),
  });

export const listComplianceVendors = () => api<VendorView[]>("/compliance/vendors");

export const createComplianceVendor = (input: {
  name: string;
  purpose: string;
  criticality?: VendorCriticality;
  dataClass?: DataClass;
}) => api<VendorView>("/compliance/vendors", { method: "POST", body: JSON.stringify(input) });

export const updateComplianceVendor = (
  id: string,
  input: Partial<
    Pick<
      ComplianceVendor,
      | "name"
      | "purpose"
      | "criticality"
      | "dataClass"
      | "ownerId"
      | "website"
      | "assurance"
      | "assuranceExpiresOn"
      | "dpaOnFile"
      | "subProcessors"
      | "reviewCadenceMonths"
      | "retiredOn"
      | "notes"
    >
  >,
) => api<VendorView>(`/compliance/vendors/${id}`, { method: "PATCH", body: JSON.stringify(input) });

export const addVendorReview = (
  id: string,
  input: { findings: string; outcome?: ReviewOutcome; reviewedOn?: string },
) => api<VendorView>(`/compliance/vendors/${id}/reviews`, {
  method: "POST",
  body: JSON.stringify(input),
});

// ── Compliance: internal audit, findings, CAPA, auditor share (phase 7) ─

export type FindingType = "major_nc" | "minor_nc" | "observation" | "ofi";
export type FindingSource =
  | "internal_audit"
  | "external_audit"
  | "incident"
  | "management_review"
  | "self_identified";
export type FindingStage =
  | "open"
  | "root_cause"
  | "action_planned"
  | "actioned"
  | "verified"
  | "closed";

export interface AuditProgrammeRecord {
  id: string;
  name: string;
  startsOn: string;
  endsOn: string;
  objectives: string | null;
}

export interface AuditEngagementRecord {
  id: string;
  programmeId: string;
  title: string;
  scope: string;
  criteria: string;
  auditorUserId: string | null;
  auditorUser?: TeamUser | null;
  auditorExternalName: string | null;
  plannedFor: string | null;
  conductedOn: string | null;
  conclusion: string | null;
}

export interface EngagementView {
  engagement: AuditEngagementRecord;
  status: "planned" | "in_progress" | "complete";
  controls: { id: string; ref: string; name: string; ownerId: string | null }[];
  findingCount: number;
}

export interface ProgrammeView {
  programme: AuditProgrammeRecord;
  engagements: EngagementView[];
  blockers: ControlBlocker[];
  coveredControlCount: number;
  applicableControlCount: number;
}

export interface CorrectiveActionRecord {
  id: string;
  findingId: string;
  rootCause: string | null;
  action: string;
  ownerId: string | null;
  owner?: TeamUser | null;
  targetDate: string | null;
  completedOn: string | null;
  completedById: string | null;
  verifiedOn: string | null;
  verifiedById: string | null;
  verifiedBy?: TeamUser | null;
  verificationNote: string | null;
}

export interface FindingRecord {
  id: string;
  ref: string;
  title: string;
  description: string;
  type: FindingType;
  source: FindingSource;
  engagementId: string | null;
  controlId: string | null;
  control?: ComplianceControl | null;
  raisedOn: string;
  raisedById: string | null;
  raisedBy?: TeamUser | null;
  closedOn: string | null;
}

export interface FindingView {
  finding: FindingRecord;
  stage: FindingStage;
  blockers: ControlBlocker[];
  actions: CorrectiveActionRecord[];
}

export interface AuditorShareRecord {
  id: string;
  label: string;
  expiresOn: string;
  allowEvidenceDownload: boolean;
  createdById: string | null;
  createdBy?: TeamUser | null;
  revokedAt: string | null;
  lastAccessedAt: string | null;
  accessCount: number;
  createdAt: string;
}

export const listAuditProgrammes = () => api<ProgrammeView[]>("/compliance/audit/programmes");

export const createAuditProgramme = (input: {
  name: string;
  startsOn: string;
  endsOn: string;
  objectives?: string;
}) => api<AuditProgrammeRecord>("/compliance/audit/programmes", {
  method: "POST",
  body: JSON.stringify(input),
});

export const createAuditEngagement = (input: {
  programmeId: string;
  title: string;
  scope: string;
  criteria: string;
  plannedFor?: string | null;
}) => api<EngagementView>("/compliance/audit/engagements", {
  method: "POST",
  body: JSON.stringify(input),
});

export const updateAuditEngagement = (
  id: string,
  input: Partial<
    Pick<
      AuditEngagementRecord,
      "title" | "scope" | "criteria" | "auditorUserId" | "auditorExternalName" | "plannedFor" | "conductedOn" | "conclusion"
    >
  >,
) => api<EngagementView>(`/compliance/audit/engagements/${id}`, {
  method: "PATCH",
  body: JSON.stringify(input),
});

export const setEngagementScope = (id: string, controlIds: string[]) =>
  api<EngagementView>(`/compliance/audit/engagements/${id}/scope`, {
    method: "POST",
    body: JSON.stringify({ controlIds }),
  });

export const listFindings = () => api<FindingView[]>("/compliance/findings");

export const createFinding = (input: {
  title: string;
  description: string;
  type: FindingType;
  source?: FindingSource;
  engagementId?: string | null;
  controlId?: string | null;
}) => api<FindingView>("/compliance/findings", { method: "POST", body: JSON.stringify(input) });

export const closeFinding = (id: string) =>
  api<FindingView>(`/compliance/findings/${id}/close`, { method: "POST" });

export const addCorrectiveAction = (
  findingId: string,
  input: { action: string; rootCause?: string | null; ownerId?: string | null; targetDate?: string | null },
) => api<FindingView>(`/compliance/findings/${findingId}/actions`, {
  method: "POST",
  body: JSON.stringify(input),
});

export const updateCorrectiveAction = (
  actionId: string,
  input: Partial<Pick<CorrectiveActionRecord, "action" | "rootCause" | "ownerId" | "targetDate" | "completedOn">>,
) => api<FindingView>(`/compliance/findings/actions/${actionId}`, {
  method: "PATCH",
  body: JSON.stringify(input),
});

export const verifyCorrectiveAction = (actionId: string, verificationNote: string) =>
  api<FindingView>(`/compliance/findings/actions/${actionId}/verify`, {
    method: "POST",
    body: JSON.stringify({ verificationNote }),
  });

export const listAuditorShares = () => api<AuditorShareRecord[]>("/compliance/auditor-shares");

export const createAuditorShare = (input: {
  label: string;
  expiresOn: string;
  allowEvidenceDownload?: boolean;
}) => api<{ share: AuditorShareRecord; token: string; url: string }>("/compliance/auditor-shares", {
  method: "POST",
  body: JSON.stringify(input),
});

export const revokeAuditorShare = (id: string) =>
  api<AuditorShareRecord>(`/compliance/auditor-shares/${id}/revoke`, { method: "POST" });

/** The auditor's own read-only bundle. No session — the token is the credential. */
export const getAuditorBundle = (token: string) =>
  api<AuditorBundle>(`/auditor/${encodeURIComponent(token)}`);

export interface AuditorBundle {
  share: { label: string; expiresOn: string; evidenceDownloadable: boolean; generatedAt: string };
  controls: {
    ref: string;
    name: string;
    description: string;
    category: string;
    frequency: string;
    testProcedure: string;
    owner: string | null;
    status: string;
    applicability: string;
    justification: string | null;
    mappings: string[];
    auditorNotes: string | null;
  }[];
  policies: {
    name: string;
    kind: string;
    owner: string | null;
    status: string;
    version: number | null;
    effectiveFrom: string | null;
    acknowledged: string;
  }[];
  periods: {
    name: string;
    startsOn: string;
    endsOn: string;
    controls: {
      ref: string;
      name: string;
      owner: string | null;
      occurrences: {
        dueOn: string;
        occurredOn: string | null;
        result: string;
        stage: string;
        evidenceCount: number;
      }[];
    }[];
  }[];
  soa: {
    ref: string;
    title: string;
    applicability: string;
    justification: string | null;
    controls: string[];
    risks: string[];
  }[];
  risks: {
    ref: string;
    title: string;
    owner: string | null;
    inherentScore: number | null;
    treatment: string | null;
    residualScore: number | null;
    status: string;
  }[];
  programmes: {
    name: string;
    startsOn: string;
    endsOn: string;
    objectives: string | null;
    coverage: string;
    engagements: {
      title: string;
      scope: string;
      criteria: string;
      auditor: string | null;
      conductedOn: string | null;
      conclusion: string | null;
      controls: string[];
      findingCount: number;
    }[];
  }[];
  findings: {
    ref: string;
    title: string;
    description: string;
    type: string;
    source: string;
    control: string | null;
    raisedOn: string;
    closedOn: string | null;
    stage: string;
    actions: {
      action: string;
      rootCause: string | null;
      targetDate: string | null;
      completedOn: string | null;
      verifiedOn: string | null;
      verificationNote: string | null;
    }[];
  }[];
}

// ── Compliance: evidence collectors (phase 9) ──────────────────────────

export type RunStatus = "ok" | "partial" | "failed";

export interface CollectionRunRecord {
  id: string;
  connectorKey: string;
  status: RunStatus;
  summary: string;
  exceptions: string[] | null;
  error: string | null;
  evidenceIds: string[] | null;
  attachedCount: number;
  triggeredById: string | null;
  ranAt: string;
}

export interface ConnectorView {
  key: string;
  label: string;
  description: string;
  controlRefs: string[];
  enabled: boolean;
  configured: boolean;
  missingEnv: string[];
  lastRunAt: string | null;
  lastRun: CollectionRunRecord | null;
}

export const listConnectors = () => api<ConnectorView[]>("/compliance/connectors");

export const listCollectionRuns = (key?: string) =>
  api<CollectionRunRecord[]>(`/compliance/connectors/runs${key ? `?key=${key}` : ""}`);

export const setConnectorEnabled = (key: string, enabled: boolean) =>
  api<ConnectorView[]>(`/compliance/connectors/${key}`, {
    method: "PATCH",
    body: JSON.stringify({ enabled }),
  });

export const runConnector = (key: string) =>
  api<CollectionRunRecord>(`/compliance/connectors/${key}/run`, { method: "POST" });
